import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LoaderCircle, MoreVertical, Plus, Search } from "lucide-react";
import { PortalActivityTemplateSummary } from "../api/contracts";
import { getActivityTypePillToken } from "../config/palette";
import { PortalLink } from "./PortalLink";

export function TemplatePicker({
  templates,
  isOpen,
  isSelecting,
  isDisabled = false,
  isLoading = false,
  error,
  onRetry,
  onOpenChange,
  onSelect,
  triggerLabel = "Select Activity Template",
  selectingLabel = "Applying template…",
  triggerVariant = "default",
}: {
  templates: readonly PortalActivityTemplateSummary[];
  isOpen: boolean;
  isSelecting: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  onOpenChange: (open: boolean) => void;
  onSelect: (templateId: string) => void;
  triggerLabel?: string;
  selectingLabel?: string;
  triggerVariant?: "default" | "primary";
}) {
  const [search, setSearch] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const pickerId = React.useId();

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      return;
    }
    if (!isLoading && !error) searchInputRef.current?.focus();
  }, [error, isLoading, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (event: MouseEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isOpen, onOpenChange]);

  const matchingTemplates = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return query
      ? templates.filter((template) =>
          template.name.toLocaleLowerCase().includes(query),
        )
      : templates;
  }, [search, templates]);
  const visibleTemplateRows = Math.min(matchingTemplates.length, 5);
  const optionsViewportHeight = visibleTemplateRows
    ? visibleTemplateRows * 40 + 5
    : undefined;

  return (
    <div
      ref={pickerRef}
      className="cp-template-picker"
      data-open={isOpen || undefined}
      data-variant={triggerVariant === "primary" ? "primary" : undefined}
      onBlur={(event) => {
        if (
          event.relatedTarget &&
          !event.currentTarget.contains(event.relatedTarget)
        ) {
          onOpenChange(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onOpenChange(false);
        }
      }}
    >
      <button
        type="button"
        className="cp-template-picker__trigger"
        data-variant={triggerVariant === "primary" ? "primary" : undefined}
        aria-expanded={isOpen}
        aria-controls={pickerId}
        aria-busy={isSelecting || undefined}
        aria-live="polite"
        disabled={isSelecting || isDisabled}
        onClick={() => onOpenChange(!isOpen)}
      >
        {isSelecting ? (
          <>
            <LoaderCircle className="cp-spin" aria-hidden="true" />
            <span>{selectingLabel}</span>
          </>
        ) : (
          <>
            {triggerVariant === "primary" ? (
              <Plus aria-hidden="true" />
            ) : null}
            <span>{triggerLabel}</span>
          </>
        )}
      </button>
      {isOpen && !isSelecting ? (
        <div className="cp-template-picker__panel" id={pickerId}>
          {isLoading ? (
            <p className="cp-template-picker__message" role="status">
              Loading activity templates…
            </p>
          ) : error ? (
            <div className="cp-template-picker__error" role="alert">
              <span>Activity templates could not be loaded.</span>
              {onRetry ? (
                <button type="button" onClick={onRetry}>
                  Try again
                </button>
              ) : null}
            </div>
          ) : (
            <>
              <label className="cp-template-picker__search">
                <span className="cp-visually-hidden">
                  Search activity templates
                </span>
                <Search aria-hidden="true" />
                <input
                  ref={searchInputRef}
                  type="search"
                  value={search}
                  placeholder="Search templates"
                  autoComplete="off"
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
              <div
                className="cp-template-picker__options"
                data-visible-rows={visibleTemplateRows}
                role="group"
                aria-label="Activity template results"
                style={{ height: optionsViewportHeight }}
              >
                {matchingTemplates.map((template) => (
                  <button
                    type="button"
                    key={template.id}
                    disabled={isSelecting}
                    onClick={() => {
                      onOpenChange(false);
                      onSelect(template.id);
                    }}
                  >
                    <span
                      className="cp-value-pill"
                      data-palette={getActivityTypePillToken(
                        template.activityType,
                      )}
                    >
                      {template.name}
                    </span>
                  </button>
                ))}
                {matchingTemplates.length === 0 ? (
                  <p>
                    {templates.length === 0
                      ? "No active templates available."
                      : "No matching templates."}
                  </p>
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function RowActionMenu({
  clinicianCode,
  editPath,
  onEdit,
  editLabel = "Edit",
  activityName,
  canEdit,
  canDuplicate = false,
  isDuplicating = false,
  onDuplicate,
  showDuplicate = true,
  canDelete = false,
  isDeleting = false,
  onDelete,
  showDelete = true,
  deleteLabel = "Delete",
  deletingLabel = "Deleting…",
}: {
  clinicianCode: string;
  editPath?: string;
  onEdit?: () => void;
  editLabel?: string;
  activityName: string;
  canEdit: boolean;
  canDuplicate?: boolean;
  isDuplicating?: boolean;
  onDuplicate?: () => void;
  showDuplicate?: boolean;
  canDelete?: boolean;
  isDeleting?: boolean;
  onDelete?: () => void;
  showDelete?: boolean;
  deleteLabel?: string;
  deletingLabel?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
    placement: "above" | "below";
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = React.useId();

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return;
    }

    const viewportPadding = 8;
    const menuGap = 4;

    const positionMenu = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;

      const triggerRect = trigger.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const belowTop = triggerRect.bottom + menuGap;
      const aboveTop = triggerRect.top - menuGap - menuRect.height;
      const fitsBelow =
        belowTop + menuRect.height <= window.innerHeight - viewportPadding;
      const fitsAbove = aboveTop >= viewportPadding;
      const placement = !fitsBelow && fitsAbove ? "above" : "below";
      const preferredTop = placement === "above" ? aboveTop : belowTop;
      const maximumTop = Math.max(
        viewportPadding,
        window.innerHeight - viewportPadding - menuRect.height,
      );
      const top = Math.min(Math.max(viewportPadding, preferredTop), maximumTop);
      const maximumLeft = Math.max(
        viewportPadding,
        window.innerWidth - viewportPadding - menuRect.width,
      );
      const left = Math.min(
        Math.max(viewportPadding, triggerRect.left),
        maximumLeft,
      );

      setMenuPosition((current) =>
        current?.top === top &&
        current.left === left &&
        current.placement === placement
          ? current
          : { top, left, placement },
      );
    };

    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);

    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [isOpen]);

  const closeAndRestoreFocus = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div
      className="cp-row-action"
      data-open={isOpen || undefined}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setIsOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          closeAndRestoreFocus();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="cp-row-action__trigger"
        aria-label={`Actions for ${activityName || "homework assignment"}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <MoreVertical aria-hidden="true" />
      </button>
      {isOpen ? (
        <div
          ref={menuRef}
          className="cp-row-action__menu"
          id={menuId}
          role="menu"
          data-placement={menuPosition?.placement}
          style={{
            top: menuPosition?.top,
            left: menuPosition?.left,
            visibility: menuPosition ? "visible" : "hidden",
          }}
        >
          {canEdit && onEdit ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setIsOpen(false);
                onEdit();
              }}
            >
              {editLabel}
            </button>
          ) : canEdit && editPath ? (
            <PortalLink
              role="menuitem"
              path={editPath}
              clinicianCode={clinicianCode}
              onClick={() => setIsOpen(false)}
            >
              {editLabel}
            </PortalLink>
          ) : (
            <button type="button" role="menuitem" disabled>
              {editLabel}
            </button>
          )}
          {showDuplicate ? (
            <button
              type="button"
              role="menuitem"
              disabled={!canDuplicate || isDuplicating}
              onClick={() => {
                setIsOpen(false);
                onDuplicate?.();
              }}
            >
              {isDuplicating ? "Duplicating…" : "Duplicate"}
            </button>
          ) : null}
          {showDelete ? (
            <>
              <span className="cp-row-action__separator" aria-hidden="true" />
              <button
                type="button"
                role="menuitem"
                disabled={!canDelete || isDeleting}
                title={
                  canDelete ? undefined : `${deleteLabel} is not available`
                }
                onClick={() => {
                  setIsOpen(false);
                  onDelete?.();
                }}
              >
                {isDeleting ? deletingLabel : deleteLabel}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function DeleteHomeworkDialog({
  homeworkName,
  isOpen,
  isDeleting,
  hasError,
  onCancel,
  onConfirm,
}: {
  homeworkName?: string;
  isOpen: boolean;
  isDeleting: boolean;
  hasError: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = React.useId();
  const descriptionId = React.useId();

  useEffect(() => {
    if (isOpen) cancelButtonRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="cp-confirm-dialog__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isDeleting) onCancel();
      }}
    >
      <div
        className="cp-confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !isDeleting) {
            event.preventDefault();
            onCancel();
          }
        }}
      >
        <h2 id={titleId}>Delete Homework?</h2>
        <p id={descriptionId}>
          Are you sure you want to delete this homework activity?
          {homeworkName ? ` “${homeworkName}” will be removed.` : ""}
        </p>
        {hasError ? (
          <p className="cp-confirm-dialog__error" role="alert">
            The homework activity could not be deleted. Please try again.
          </p>
        ) : null}
        <div className="cp-confirm-dialog__actions">
          <button
            ref={cancelButtonRef}
            type="button"
            className="cp-button cp-button--secondary"
            disabled={isDeleting}
            onClick={onCancel}
          >
            No
          </button>
          <button
            type="button"
            className="cp-button cp-button--danger"
            disabled={isDeleting}
            onClick={onConfirm}
          >
            {isDeleting ? "Deleting…" : "Yes"}
          </button>
        </div>
      </div>
    </div>
  );
}
